import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, GitBranch, Loader2, Sparkles } from 'lucide-react';
import {
  resolutionApi,
  type OntologicalSchemaEntry,
} from '@/services/api';
import { useCatalogsStore } from '@/stores/catalogs';
import { UOSClassGrid } from '@/components/knowledge/catalogs/UOSClassGrid';
import { DistributionChart } from '@/components/knowledge/catalogs/DistributionChart';
import { ProposedExtensionsList } from '@/components/knowledge/catalogs/ProposedExtensionsList';
import { GlobalConceptsTab } from '@/components/knowledge/catalogs/GlobalConceptsTab';

type Tab = 'classes' | 'distribution' | 'proposed' | 'global';

export function OntologySchemaPage() {
  const [tab, setTab] = useState<Tab>('classes');
  const [schema, setSchema] = useState<Record<string, OntologicalSchemaEntry>>({});
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [distribution, setDistribution] = useState<Record<string, number>>({});
  const [classifyBusy, setClassifyBusy] = useState(false);
  const [classifyStatus, setClassifyStatus] = useState<string | null>(null);
  const { summary, fetchSummary } = useCatalogsStore();

  useEffect(() => {
    setSchemaLoading(true);
    Promise.all([
      resolutionApi.getOntologicalSchema().catch(() => null),
      resolutionApi.getOntologicalDistribution().catch(() => null),
    ])
      .then(([schemaRes, distRes]) => {
        if (schemaRes) setSchema(schemaRes.classes);
        if (distRes) setDistribution(distRes.distribution);
      })
      .finally(() => setSchemaLoading(false));
    fetchSummary();
  }, [fetchSummary]);

  const pendingCount = summary.pending_by_source.uos;

  const labels = Object.fromEntries(
    Object.entries(schema).map(([k, v]) => [k, v.label ?? k]),
  );

  const handleClassifyAll = async () => {
    setClassifyBusy(true);
    setClassifyStatus(null);
    try {
      await resolutionApi.classifyAll();
      setClassifyStatus('Classification job started — results stream to canonical entities');
    } catch (e) {
      setClassifyStatus((e as Error).message);
    } finally {
      setClassifyBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge/catalogs"
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} />
            Catalogs
          </Link>
          <div className="flex items-center gap-2">
            <GitBranch size={18} className="text-brand-teal" />
            <div>
              <h1 className="text-xl font-semibold text-navy-900">
                Universal Ontological Schema
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Philosophical classification of all canonical knowledge
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={handleClassifyAll}
          disabled={classifyBusy}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 disabled:opacity-40 transition-colors shadow-sm"
        >
          {classifyBusy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Sparkles size={14} />
          )}
          Classify All Unclassified
        </button>
      </div>

      {classifyStatus && (
        <div className="px-6 py-2 text-xs bg-brand-teal/5 border-b border-brand-teal/10 text-brand-teal-dark">
          {classifyStatus}
        </div>
      )}

      <div className="flex border-b border-slate-200 bg-white px-6">
        <TabButton active={tab === 'classes'} onClick={() => setTab('classes')}>
          UOS Classes
          <span className="ml-1.5 text-[10px] text-slate-400">
            ({Object.keys(schema).length})
          </span>
        </TabButton>
        <TabButton active={tab === 'distribution'} onClick={() => setTab('distribution')}>
          Distribution
        </TabButton>
        <TabButton active={tab === 'proposed'} onClick={() => setTab('proposed')}>
          Proposed Extensions
          {pendingCount > 0 && (
            <span className="ml-1.5 text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'global'} onClick={() => setTab('global')}>
          Global Concepts
        </TabButton>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {tab === 'classes' && (
          <>
            {schemaLoading ? (
              <div className="text-sm text-slate-400 text-center py-8">
                <Loader2 size={14} className="animate-spin inline mr-2" /> Loading schema...
              </div>
            ) : (
              <UOSClassGrid schema={schema} distribution={distribution} />
            )}
          </>
        )}
        {tab === 'distribution' && (
          <DistributionChart distribution={distribution} labels={labels} />
        )}
        {tab === 'proposed' && <ProposedExtensionsList schema={schema} />}
        {tab === 'global' && <GlobalConceptsTab />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center px-4 py-2.5 text-sm font-medium transition-colors',
        active
          ? 'text-brand-teal border-b-2 border-brand-teal'
          : 'text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
